import {
  CountrySelectFormField,
  DateFormField,
  Form,
  FormSubmitButton,
  PlacesAutocompleteFormField,
  SelectFormField,
  StateSelectFormField,
  TextAreaFormField,
  TextFormField,
} from "@/components/ui";
import { AppModal } from "@/components/ui/AppModal/AppModal";
import { FormLoadingOverlay } from "@/components/ui/Form/FormLoadingOverlay";
import { FormCancelButton } from "@/components/ui/FormFields/FormCancelButton/FormCancelButton";
import { TimeFormField } from "@/components/ui/FormFields/TimeFormField/TimeFormField";
import {
  retrieveMatterQueryOptions,
  useUpdateMatter,
} from "@/lib/data/react-query/matters/matter";
import type { Matter } from "@/lib/types";
import { cn } from "@/lib/utils/cn";
import { slideoverFieldsetClassName } from "@/lib/utils/slideover-classes";
import { zodResolver } from "@hookform/resolvers/zod";
import { useSuspenseQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

interface CaseIncidentDescriptionFormModalProps {
  matterId: string;
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
}

const incidentFacts = [
  { name: "clientMinorAtIncident", label: "Client minor at incident" },
  { name: "deathOccurredFromIncident", label: "Death occurred from incident" },
  { name: "governmentEntityInvolved", label: "Government entity involved" },
] as const;
const incidentFactOptions = [
  { id: "unspecified", label: "Not specified" },
  { id: "yes", label: "Yes" },
  { id: "no", label: "No" },
];
const incidentFactSchema = z.enum(["unspecified", "yes", "no"]);

const formSchema = z.object({
  clientMinorAtIncident: incidentFactSchema,
  deathOccurredFromIncident: incidentFactSchema,
  governmentEntityInvolved: incidentFactSchema,
  dateOfLoss: z.string().min(1, { message: "Date of loss is required" }),
  incidentAddressCity: z.string().optional().or(z.literal("")),
  incidentAddressCountry: z.string().min(1, { message: "Country is required" }),
  incidentAddressCounty: z.string().optional().or(z.literal("")),
  incidentAddressDetails: z.string().optional().or(z.literal("")),
  incidentAddressState: z.string().min(1, { message: "State is required" }),
  incidentAddressStreet1: z.string().optional().or(z.literal("")),
  incidentAddressStreet2: z.string().optional().or(z.literal("")),
  incidentAddressZip: z.string().optional().or(z.literal("")),
  incidentDescription: z.string().optional().or(z.literal("")),
  incidentTime: z.string().optional().or(z.literal("")),
});

const defaultFormValues = {
  incidentAddressCountry: "United States",
  incidentAddressCity: "",
  incidentAddressState: "",
  incidentAddressZip: "",
  incidentAddressStreet1: "",
  incidentAddressStreet2: "",
  incidentAddressCounty: "",
  incidentAddressDetails: "",
  incidentDescription: "",
  dateOfLoss: "",
  incidentTime: "",
};

const INCIDENT_DETAILS_MODAL_FORM_ID = "case-incident-description-modal-form";

export const CaseIncidentDescriptionFormModal = ({
  matterId,
  isOpen,
  setIsOpen,
}: CaseIncidentDescriptionFormModalProps) => {
  const { data: matterData } = useSuspenseQuery(
    retrieveMatterQueryOptions(matterId),
  );
  const matter = matterData.data;
  const updateMatter = useUpdateMatter();

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    values: {
      ...defaultFormValues,
      ...matter,
      clientMinorAtIncident:
        matter.clientMinorAtIncident == null
          ? "unspecified"
          : matter.clientMinorAtIncident
            ? "yes"
            : "no",
      deathOccurredFromIncident:
        matter.deathOccurredFromIncident == null
          ? "unspecified"
          : matter.deathOccurredFromIncident
            ? "yes"
            : "no",
      governmentEntityInvolved:
        matter.governmentEntityInvolved == null
          ? "unspecified"
          : matter.governmentEntityInvolved
            ? "yes"
            : "no",
      dateOfLoss: matter.dateOfLoss ?? "",
      incidentAddressCountry: matter.incidentAddressCountry ?? "United States",
      incidentAddressState: matter.incidentAddressState ?? "",
      incidentAddressCity: matter.incidentAddressCity ?? "",
      incidentAddressCounty: matter.incidentAddressCounty ?? "",
      incidentAddressDetails: matter.incidentAddressDetails ?? "",
      incidentAddressStreet1: matter.incidentAddressStreet1 ?? "",
      incidentAddressStreet2: matter.incidentAddressStreet2 ?? "",
      incidentAddressZip: matter.incidentAddressZip ?? "",
      incidentDescription: matter.incidentDescription ?? "",
      incidentTime: matter.incidentTime ?? "",
    },
    mode: "onSubmit",
  });

  const handleSubmitIncidentDescription = async (
    data: z.infer<typeof formSchema>,
  ) => {
    const {
      clientMinorAtIncident,
      deathOccurredFromIncident,
      governmentEntityInvolved,
      ...incidentDetails
    } = data;
    const factValues = {
      clientMinorAtIncident,
      deathOccurredFromIncident,
      governmentEntityInvolved,
    };
    const factUpdates: Partial<
      Pick<Matter, (typeof incidentFacts)[number]["name"]>
    > = {};
    for (const { name } of incidentFacts) {
      const value =
        factValues[name] === "unspecified" ? null : factValues[name] === "yes";
      if (value !== (matter[name] ?? null)) factUpdates[name] = value;
    }
    try {
      await updateMatter.mutateAsync({
        ...incidentDetails,
        ...factUpdates,
        dateModified: matter.dateModified,
        id: matter.id,
      });
      setIsOpen(false);
    } catch {
      // Toast + rollback handled by useUpdateMatter; keep modal open
    }
  };

  useEffect(() => {
    if (!isOpen) {
      form.reset();
    }
  }, [isOpen, form]);

  const modalFooter = (
    <div className="flex w-full flex-wrap justify-end gap-2">
      <FormCancelButton setOpen={setIsOpen} isDirty={form.formState.isDirty} />
      <FormSubmitButton
        loading={updateMatter.isPending}
        submitFormId={INCIDENT_DETAILS_MODAL_FORM_ID}
      />
    </div>
  );

  const fieldset = (
    <fieldset
      className={cn(
        slideoverFieldsetClassName,
        "grid-cols-1 md:grid-cols-2",
        "my-0 p-0",
      )}
    >
      <DateFormField
        register={form.register("dateOfLoss")}
        label="Date of loss*"
        name="dateOfLoss"
      />
      <TimeFormField
        name="incidentTime"
        label="Incident time"
        register={form.register("incidentTime")}
      />
      <PlacesAutocompleteFormField
        register={form.register("incidentAddressStreet1")}
        defaultValue={form.getValues("incidentAddressStreet1")}
        label={`Incident address 1*`}
        onAddressSelect={(address) => {
          form.setValue("incidentAddressCity", address.city, {
            shouldValidate: true,
            shouldDirty: true,
          });
          form.setValue("incidentAddressCounty", address.county, {
            shouldValidate: true,
          });
          form.setValue("incidentAddressState", address.state, {
            shouldValidate: true,
          });
          form.setValue("incidentAddressZip", address.zipCode, {
            shouldValidate: true,
          });
          form.setValue("incidentAddressStreet1", address.streetAddress, {
            shouldValidate: true,
          });
          form.setValue("incidentAddressCountry", address.country, {
            shouldValidate: true,
          });
          form.setValue("incidentAddressStreet2", "");
        }}
      />
      <TextFormField
        register={form.register("incidentAddressStreet2")}
        label={`Incident address 2`}
      />
      <TextFormField
        register={form.register("incidentAddressZip")}
        label={`Incident zip code`}
      />
      <TextFormField
        register={form.register("incidentAddressCity")}
        label={`Incident city`}
      />
      <TextFormField
        register={form.register("incidentAddressCounty")}
        label={`Incident county`}
      />
      <StateSelectFormField
        register={form.register("incidentAddressState")}
        label={`Incident state*`}
        name="incidentAddressState"
        country={form.watch("incidentAddressCountry")}
      />
      <CountrySelectFormField
        register={form.register("incidentAddressCountry")}
        label={`Incident country*`}
        name="incidentAddressCountry"
        stateSelectName="incidentAddressState"
      />
      {incidentFacts.map(({ name, label }) => (
        <SelectFormField
          key={name}
          label={label}
          register={form.register(name)}
          items={incidentFactOptions}
          itemsKey="id"
          formatItemLabel={(item) => item?.label}
        />
      ))}
      <p className="text-muted-foreground col-span-full text-sm">
        Answer these incident facts to calculate a statute estimate. Leave
        unknown facts as Not specified.
      </p>
      <TextAreaFormField
        register={form.register("incidentAddressDetails")}
        label="Incident location details"
        className="col-span-2"
        textAreaClassName="field-sizing-content max-h-[20lh]"
      />
      <TextAreaFormField
        register={form.register("incidentDescription")}
        label="Incident description"
        className="col-span-2"
        textAreaClassName="field-sizing-content max-h-[20lh]"
      />
    </fieldset>
  );

  return (
    <Form {...form}>
      <AppModal
        open={isOpen}
        onOpenChange={setIsOpen}
        title="Incident details"
        size="2xl"
        className="w-[calc(100vw-2rem)] max-w-4xl"
        loading={updateMatter.isPending}
        isDirty={form.formState.isDirty}
        footer={modalFooter}
      >
        <form
          id={INCIDENT_DETAILS_MODAL_FORM_ID}
          className="relative flex min-h-0 flex-1 flex-col"
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            void form.handleSubmit(handleSubmitIncidentDescription)();
          }}
        >
          {updateMatter.isPending && <FormLoadingOverlay />}
          {fieldset}
        </form>
      </AppModal>
    </Form>
  );
};
